<template>
  <section class="irp-key-contacts-panel" data-testid="irp-key-contacts-panel">
    <h2 class="irp-key-contacts-panel__title">Recommended Key Contacts</h2>
    <div
      v-if="isLoading"
      class="irp-key-contacts-panel__skeletons"
      data-testid="irp-key-contacts-skeletons"
    >
      <div
        v-for="skeleton in CONTACT_COUNT"
        :key="skeleton"
        class="irp-key-contacts-panel__skeleton"
        data-testid="irp-key-contacts-skeleton"
      >
        <BittsBone type="avatar" size="x-small" />
        <div class="irp-key-contacts-panel__skeleton-lines">
          <BittsBone type="heading" class="w-1/3" />
          <BittsBone type="copy" />
        </div>
      </div>
    </div>
    <BittsEmptyState
      v-else-if="hasError"
      :font-awesome-icon="ERROR_STATE.icon"
      :title="ERROR_STATE.title"
      :description="ERROR_STATE.description"
      :button="RETRY_BUTTON"
      data-testid="irp-key-contacts-error-state"
      @button-clicked="reloadContacts"
    />
    <BittsEmptyState
      v-else-if="!keyContacts.length"
      :font-awesome-icon="EMPTY_STATE.icon"
      :title="EMPTY_STATE.title"
      :description="EMPTY_STATE.description"
      data-testid="irp-key-contacts-empty-state"
    />
    <BittsCard v-else class="irp-key-contacts-panel__card" :no-padding="false">
      <div class="irp-key-contacts-panel__body ph-no-capture dd-privacy-mask">
        <IRPKeyContact
          v-for="group in keyContacts"
          :key="group.email"
          :contact="group.first_contact"
          class="irp-key-contacts-panel__contact"
        />
      </div>
      <RouterLink :to="viewContactsRoute">
        <BittsButton
          :text="viewContactsText"
          type="neutral"
          variant="outline"
          size="x-small"
          data-testid="irp-key-contacts-view-all"
        />
      </RouterLink>
    </BittsCard>
  </section>
</template>

<script setup lang="ts">
import {
  BittsBone,
  BittsButton,
  BittsCard,
  BittsEmptyState,
} from '@crossbeam/bitts';
import type { BittsIcon } from '@crossbeam/bitts';

import { pluralize } from 'humanize-plus';
import { computed, ref, watch } from 'vue';

import { defaultErrorMessage } from '@/errors';
import { useCopilotContactsStore, useIndividualRecordStore } from '@/stores';

import { EXPAND_ALL_QUERY, IRP_TAB, IRP_TAB_ROUTES } from './irp';
import IRPKeyContact from './IRPKeyContact.vue';
import { useProvideHasSalesforceFeed } from './useProvideHasSalesforceFeed';
import { useRecordRouteParams } from './useRecordRouteParams';

const CONTACT_COUNT = 3;

const EMPTY_STATE: { title: string; description: string; icon: BittsIcon } = {
  title: 'No partner contacts yet',
  description:
    'Contacts that your partners share with you for this account will appear here.',
  icon: ['fak', 'contacts'],
};

const ERROR_STATE: { title: string; description: string; icon: BittsIcon } = {
  title: 'Key contacts could not load',
  description: defaultErrorMessage,
  icon: ['fak', 'warning'],
};

const RETRY_BUTTON = {
  text: 'Try again',
  type: 'neutral',
  variant: 'outline',
} as const;

const recordParams = useRecordRouteParams();

const viewContactsRoute = computed(() => ({
  name: IRP_TAB_ROUTES[IRP_TAB.contacts],
  params: recordParams.value,
  query: EXPAND_ALL_QUERY,
}));

const individualRecordStore = useIndividualRecordStore();
const contactsStore = useCopilotContactsStore();

const loadedRecordId = ref<string | null>(null);

let requestToken = 0;

const isLoading = computed(
  () => loadedRecordId.value !== individualRecordStore.sourcePrimaryKey,
);

const hasError = computed(() => !isLoading.value && !!contactsStore.error);

const contactGroups = computed(() =>
  isLoading.value ? [] : contactsStore.contactsGroupedByEmail,
);

const contactCount = computed(() => contactGroups.value.length);

const viewContactsText = computed(
  () =>
    `View ${contactCount.value} ${pluralize(contactCount.value, 'contact')}`,
);

const keyContacts = computed(() => contactGroups.value.slice(0, CONTACT_COUNT));

async function loadContacts(sourceId: number | null, recordId: string | null) {
  requestToken += 1;
  const token = requestToken;
  loadedRecordId.value = null;
  if (!recordId) return;

  await contactsStore.setRecordContext({ recordId, sourceId });
  if (token !== requestToken) return;
  loadedRecordId.value = recordId;
}

async function reloadContacts() {
  await loadContacts(
    individualRecordStore.sourceId,
    individualRecordStore.sourcePrimaryKey,
  );
}

watch(
  () =>
    [
      individualRecordStore.sourceId,
      individualRecordStore.sourcePrimaryKey,
    ] as const,
  ([sourceId, recordId]) => loadContacts(sourceId, recordId),
  { immediate: true },
);

useProvideHasSalesforceFeed();
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";
.irp-key-contacts-panel {
  @apply flex flex-col gap-8;
}

.irp-key-contacts-panel__title {
  @apply text-lg font-bold text-neutral-text-strong;
}

.irp-key-contacts-panel__card {
  @apply flex flex-col justify-between gap-16 p-24;
}

.irp-key-contacts-panel__body {
  @apply flex flex-col gap-16;
}

.irp-key-contacts-panel__contact {
  @apply border-b border-neutral-border pb-16;

  &:last-child {
    @apply border-b-0 pb-0;
  }
}

.irp-key-contacts-panel__skeletons {
  @apply flex flex-col gap-16 rounded-bts-lg border border-neutral-border p-24;
}

.irp-key-contacts-panel__skeleton {
  @apply flex items-start gap-8;
}

.irp-key-contacts-panel__skeleton-lines {
  @apply flex flex-col gap-4 flex-1;
}
</style>
